import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { deepEqual } from 'fast-equals';
import sanitize from 'sanitize-html';
import debounce from 'lodash.debounce';

import { cn } from 'helpers/classnames';
import { BEST_FOR_YOU_FACETFIELD } from 'constants/appConstants';
import { SAVI_COOKIE, SAVI_SIZE_COOKIE, SEARCH_BEST_FOR_YOU_COOKIE } from 'constants/cookies';
import { evProductInteract, evSortSearchClick } from 'events/search';
import { fetchAccountInfo } from 'actions/account/account';
import marketplace from 'cfg/marketplace.json';
// actions
import { addAdToQueue, updateAdData } from 'actions/ads';
import { fetchProductDetail } from 'actions/productDetail';
import { deleteSavedFilters, fetchFromSearch, fetchFromZso, makeSearchPixelFacetDataFromState, saveFilters } from 'actions/fancyUrls';
import { clearAutoComplete } from 'actions/autoComplete';
import { fireSearchPixels, setOosMessaging, trackSponsoredAdImpressions } from 'actions/products';
import { redirectToAuthenticationFor, redirectWithAppRoot } from 'actions/redirect';
import { removeFromStoredCookies, sessionExpiration, setAndStoreCookie } from 'actions/session';
import { fireExplicitSearchEventFromLocalStorage } from 'helpers/searchInputUtils';
import { getHearts, heartProduct, toggleHeartingLoginModal, unHeartProduct } from 'actions/hearts';
import {
  clearInlineRecos,
  fetchSearchInlineRecos,
  searchFeedbackClick,
  setUrlUpdated,
  toggleFacetsContainer,
  toggleSavedFilters,
  updateBestForYou,
  updateSort
} from 'actions/search';
import {
  getFacetUrlParams,
  getFacetUrlPath,
  removePersonalizedSize,
  resetFacetGroup,
  togglePersonalizedSize,
  toggleSelectedFacet,
  toggleSelectedFacetGroup
} from 'actions/facets';
import { pageTypeChange } from 'actions/common';
import { setHFSearchTerm } from 'actions/headerfooter';
import { fetchSymphonySearchComponents } from 'store/ducks/search/actions';
import { fetchLandingPageInfo } from 'actions/landing/landingPageInfo.js';
// resources
import { track } from 'apis/amethyst';
import { ensureClass, removeCookie, sanitizeForEvent, setCookie } from 'helpers';
import { apstagRemoveToken, apstagUpdateToken, SRP_NARROW_TOP, SRP_WIDE_MID, SRP_WIDE_TOP } from 'helpers/apsAdvertisement';
import { isDesktop } from 'helpers/ClientUtils';
import { onEvent } from 'helpers/EventHelpers';
import { trackEvent, trackLegacyEvent } from 'helpers/analytics';
import {
  buildSearchTermEventPayload,
  createClearAllFiltersQuery,
  formatMicrosoftPixelData,
  formatProductClickEvent,
  formatSavedFilters,
  sortStringToObject,
  updateSortParamOfUrl
} from 'helpers/SearchUtils';
// containers/components
import MelodyCategory from 'components/landing/MelodyCategory';
import HtmlToReact from 'components/common/HtmlToReact';
import JanusPixel from 'components/common/JanusPixel';
import GamSlot from 'components/common/GamSlot';
import SearchHeader from 'components/search/SearchHeader';
import CompactSingleSelects from 'components/search/CompactSingleSelects';
import { FacetsSidebar } from 'components/search/Facets/FacetsSidebar';
import Pagination from 'components/common/Pagination';
import TopBannerAd from 'components/search/TopBannerAd';
import SearchLogic from 'containers/SearchLogic';
import Products from 'components/search/Products';
import SiteAwareMetadata from 'components/SiteAwareMetadata';
import SkipLinks from 'components/common/SkipLinks';
import Tooltip from 'components/common/Tooltip';
import InfoOutlineSmallIcon from 'components/common/IconsV2/InfoOutlineSmallIcon';
import { fetchCustomerAuthDetails } from 'actions/authentication';
import { clearAllAutofacets, removeAutofacetFilter } from 'actions/autofacet';
import { evSearchClearFilters } from 'events/headerFooter';
import { BannerAdInTest } from 'components/common/BannerAd/BannerAd';
import { fireAmplitudeProductClickedEvent, fireAmplitudeProductFilterEvent } from 'utils/amplitude';
import {
  AMPLITUDE_FILTER_ADDED,
  AMPLITUDE_FILTER_REMOVED,
  AMPLITUDE_PRODUCT_CLICKED,
  AMPLITUDE_PRODUCT_CLICKED_EVENT_ID,
  GTM_EVENT_KEY
} from 'constants/amplitude';
import { getTrackingDataForFilterEvent } from 'helpers/amplitude';
import { firePixelEvent } from 'actions/pixelServer';
import { FacetsDrawer } from 'components/search/Facets/FacetsDrawer';
import { FacetsProvider } from 'components/search/Facets/FacetsProvider';
import { SortDrawer } from 'components/search/SortDrawer/SortDrawer';
import SearchFeedbackBottom from 'components/search/SearchFeedbackBottom/SearchFeedbackBottom';

// eslint-disable-next-line css-modules/no-unused-class
import css from 'styles/containers/search.scss';

// marketplace
const {
  hasApstagAdsToken,
  cookieDomain,
  features: { showRatings },
  search: { showRatingStars, showSeoText, sortOptions },
  name
} = marketplace;

const SYMPHONY_TOP_HEADER_SLOT_NAME = 'search-header-1';

export class StandardSearch extends Component {
  static fetchDataOnServer(store, location, params, fetchParams = {}) {
    const state = store.getState();
    const { cookies } = state;

    if (!cookies?.[SAVI_COOKIE]) {
      store.dispatch(toggleSavedFilters());
    }

    return SearchLogic.fetchDataOnServer(store, location, params, fetchParams);
  }

  static afterFetchDataOnServer(store) {
    return SearchLogic.afterFetchDataOnServer(store);
  }

  constructor(props) {
    super(props);
    this.searchHeaderRef = React.createRef();
    this.debouncedUpdateHeaderHeight = debounce(this.updateHeaderHeight, 250);
    this.pendingAmplitudeEvent = null;
  }

  state = {
    seoCopyCollapsed: true,
    feedbackSubmitted: false,
    searchHeaderHeight: 0,
    isSortOptionUpdating: false
  };

  componentDidMount() {
    const {
      cookieDomain,
      saveFilters,
      saviSizeCookie,
      isShowingThirdPartyAds,
      isRecognizedCustomer,
      fetchAccountInfo,
      fetchCustomerAuthDetails,
      customerId,
      adCustomerId,
      adEmailHash,
      updateAdData
    } = this.props;

    // todo
    /* This "apstagRemoveToken" function is added to remove the cookies that was generated to personalise the apstag ads on 6pm site. Currently the functionality
        is turned off but the cookies are not yet removed. They will automatically get removed after their lifetime, i.e. 14 days from turn off - we
        can remove this function after 9th Feb 2023. We are adding this function to make sure that users are not getting personalised content when
        personalisation is turned off from our side. */
    if (name === '6pm.com') {
      apstagRemoveToken();
    }

    if (hasApstagAdsToken) {
      apstagUpdateToken(customerId, adCustomerId, adEmailHash, fetchAccountInfo, updateAdData, fetchCustomerAuthDetails);
    }

    fireExplicitSearchEventFromLocalStorage();

    if (saviSizeCookie && isRecognizedCustomer) {
      removeCookie(SAVI_SIZE_COOKIE, cookieDomain);
      saveFilters(null, true);
    }

    if (isShowingThirdPartyAds) {
      this.includeApsAds();
    }

    this.debouncedUpdateHeaderHeight();
    window.addEventListener('resize', this.updateHeaderHeight);

    // resets facet to account for menu change at m-tabletPortrait
    this.resizeListener = window.matchMedia('(max-width: 768px)');
    this.resizeListener.addListener(this.resetFacet);
  }

  componentDidUpdate(prevProps) {
    // Mobile sticky header
    ensureClass(document.body, 'fixedSearchMenu');

    // If search term changes at all, reset the feedback form for another submission
    const {
      filters: { originalTerm, savedsizes, executedSearchUrl },
      location
    } = this.props;

    const {
      filters: { originalTerm: prevOriginalTerm, executedSearchUrl: prevExecutedSearchUrl },
      location: oldLocation
    } = prevProps;

    const locationsDiffer = !deepEqual(location, oldLocation);
    // Check if this is a new search request (URL changed)
    const isNewSearchRequest = executedSearchUrl !== prevExecutedSearchUrl;

    if (originalTerm !== prevOriginalTerm) {
      this.setState({ feedbackSubmitted: false });
    }

    if (locationsDiffer) {
      if (savedsizes && Object.values(savedsizes.filters).length && savedsizes.id && !this.sentSavedSizeImpression) {
        trackEvent('TE_SAVED_FILTERS_VISIBLE');
        this.sentSavedSizeImpression = true;
      } else if (this.sentSavedSizeImpression) {
        this.sentSavedSizeImpression = false;
      }
    }

    if (prevProps.filters !== this.props.filters) {
      this.debouncedUpdateHeaderHeight();

      const prevFilters = prevProps.filters.selected;
      const currentFilters = this.props.filters.selected;

      // Count total selected values (not just groups)
      const prevCount = Object.values(prevFilters.singleSelects).flat().length + Object.values(prevFilters.multiSelects).flat().length;
      const currentCount = Object.values(currentFilters.singleSelects).flat().length + Object.values(currentFilters.multiSelects).flat().length;

      // Check if any filter values actually changed
      const filtersChanged = JSON.stringify(prevFilters) !== JSON.stringify(currentFilters);

      // Only track filter changes if this is NOT a new search request
      // New search requests should only fire search events, not filter events
      if (filtersChanged && !isNewSearchRequest) {
        if (currentCount > prevCount) {
          this.pendingAmplitudeEvent = AMPLITUDE_FILTER_ADDED;
        } else if (currentCount < prevCount) {
          this.pendingAmplitudeEvent = AMPLITUDE_FILTER_REMOVED;
        } else {
          // Same count but different values (e.g., switching brands)
          this.pendingAmplitudeEvent = AMPLITUDE_FILTER_ADDED;
        }
      }
    }

    // Fire amplitude event when products finish loading
    if (prevProps.products.isLoading && !this.props.products.isLoading && this.pendingAmplitudeEvent) {
      this.fireAmplitudeFilterEvent(this.pendingAmplitudeEvent);
      this.pendingAmplitudeEvent = null;
    }

    // Reset loading state when products finish loading
    if (prevProps.products.isLoading && !this.props.products.isLoading && this.state.isSortOptionUpdating) {
      this.setState({ isSortOptionUpdating: false });
    }
  }

  componentWillUnmount() {
    this.resizeListener.removeListener(this.resetFacet);
    window.removeEventListener('resize', this.debouncedUpdateHeaderHeight);
    this.debouncedUpdateHeaderHeight.cancel();
  }

  sentSavedSizeImpression = false;

  updateHeaderHeight = () => {
    if (this.searchHeaderRef.current && !isDesktop()) {
      const height = this.searchHeaderRef.current.offsetHeight;
      this.setState({ searchHeaderHeight: height });
    } else {
      this.setState({ searchHeaderHeight: 0 });
    }
  };

  includeApsAds = () => {
    const { addAdToQueue } = this.props;
    const slots = [{ name: SRP_WIDE_TOP }, { name: SRP_WIDE_MID }, { name: SRP_NARROW_TOP }];

    addAdToQueue(slots);
  };

  onPagination = page => {
    const { trackEvent } = this.props;
    trackEvent('TE_SEARCH_PAGINATION', `${page}`);
  };

  makePagination = () => {
    const { filters } = this.props;
    return (
      filters.pageCount > 1 && (
        <Pagination
          firstPageIndex={0}
          page={filters.page}
          filters={filters}
          compact={false}
          smallerButtons={true}
          totalPages={filters.pageCount}
          onPagination={this.onPagination}
          useSearchPageStyles={true}
        />
      )
    );
  };

  searchScrollTop = () => {
    if (!window) return;

    const currentTop = window.scrollY;
    if (currentTop <= 1) {
      return;
    }

    const step = Math.max(currentTop / 10, 1);
    window.scrollTo(0, Math.max(currentTop - step, 0));

    // Using requestAnimationFrame for better performance
    requestAnimationFrame(() => this.searchScrollTop());
  };

  makeScrollButton = () => {
    const { testId } = this.context;
    return (
      <button
        type="button"
        ref={el => (this.backToTop = el)}
        className={css.backToTop}
        onClick={this.searchScrollTop}
        aria-label="scroll to top"
        data-test-id={testId('scrollToTop')}
      />
    );
  };

  makeSearchFooter = () => {
    const { products } = this.props;
    const { testId, marketplace } = this.context;
    const showSearchFeedback = marketplace?.search?.showSearchFeedback ?? false;
    const shouldShowFeedback = Boolean(products.totalProductCount) && Boolean(showSearchFeedback);
    return (
      <div className={css.searchFooterWrapper} id="searchPagination">
        {this.makePersonalizedSortToggle('bestForYouMobileMessaging', true)}
        {this.makePagination()}
        {shouldShowFeedback && <SearchFeedbackBottom testId={testId} />}
        {this.makeSeoCopyBottomPosition()}
      </div>
    );
  };

  makeScrollhandler = () => {
    const { backToTop } = this;
    if (window && document) {
      onEvent(
        document,
        'scroll',
        () => {
          const scrollTop = window.scrollY;
          if (scrollTop > 200 && !backToTop.classList.contains(css.visible)) {
            backToTop.classList.add(css.visible);
          } else if (scrollTop < 200 && backToTop.classList.contains(css.visible)) {
            backToTop.classList.remove(css.visible);
          }
        },
        null,
        this
      );
    }
  };

  onSortSelected = ({ target }) => {
    const {
      filters,
      trackEvent,
      cookieDomain,
      trackLegacyEvent,
      updateSort,
      updateBestForYou,
      getFacetUrlPath,
      getFacetUrlParams,
      products: { list },
      pixelFacetData,
      firePixelEvent
    } = this.props;

    this.setState({ isSortOptionUpdating: true });

    if (target.value === 'bestForYou-desc') {
      setCookie(SEARCH_BEST_FOR_YOU_COOKIE, '', { domain: cookieDomain });
      updateBestForYou(true);
    } else {
      updateBestForYou(false);
    }

    updateSort(sortStringToObject(target.value));

    const updatedZsoUrl = updateSortParamOfUrl(filters.executedSearchUrl, target.value);
    getFacetUrlPath(updatedZsoUrl);
    getFacetUrlParams(updatedZsoUrl);

    const payload = buildSearchTermEventPayload(filters.term);
    const { label } = (target[target.selectedIndex] || target).dataset;
    const sortByEventInfo = sanitizeForEvent(label);
    trackLegacyEvent('Search-Results-Page', `Sort-By-${sortByEventInfo}`, payload);
    trackEvent('TE_SORTMENU_SELECTSORT', sortByEventInfo);
    track(() => [
      evSortSearchClick,
      {
        sortType: label
      }
    ]);

    const eventData = {
      stringResults: formatMicrosoftPixelData({ filters, results: list }),
      results: list.map(result => ({
        sku: result.productId,
        styleId: result.styleId
      })),
      facets: pixelFacetData,
      term: filters.term
    };

    firePixelEvent('search', eventData);
  };

  runToggleFacetsContainer = opening => {
    const { toggleFacetsContainer } = this.props;
    toggleFacetsContainer(opening);
  };

  onApplyFilters = () => {
    const { trackEvent } = this.props;
    this.resetFacet();
    trackEvent('TE_SEARCH_APPLYFILTERS');
  };

  makeAccessibilityAnchors = hasProductResults => {
    const {
      filters: {
        selected: { singleSelects, multiSelects }
      },
      toggleFacetsContainer
    } = this.props;
    if (hasProductResults()) {
      const links = [
        {
          id: 'searchPage',
          value: 'Skip to search results'
        },
        {
          id: 'searchFilters',
          value: 'Skip to filters',
          callback: () => !isDesktop() && toggleFacetsContainer(true)
        },
        {
          id: 'searchSort',
          value: 'Skip to sort'
        },
        {
          id: 'searchSelectedFilters',
          value: !!Object.keys(singleSelects).length || !!Object.keys(multiSelects).length ? 'Skip to selected filters' : null
        }
      ];
      return <SkipLinks links={links} />;
    }
    return null;
  };

  handlePersonalizedBestForYou = () => {
    const { updateBestForYou, cookieDomain, filters, updateSort, getFacetUrlPath, getFacetUrlParams } = this.props;
    const isNewBestForYouStateActive = !filters.bestForYou || !filters.sort.bestForYou;
    const isBestForYouActive = isNewBestForYouStateActive ? 'bestForYou-desc' : 'relevance-desc';
    const updatedZsoUrl = updateSortParamOfUrl(filters.executedSearchUrl, isBestForYouActive);
    getFacetUrlPath(updatedZsoUrl);
    getFacetUrlParams(updatedZsoUrl);
    updateBestForYou(isNewBestForYouStateActive);
    updateSort(isNewBestForYouStateActive ? { bestForYou: 'desc' } : { relevance: 'desc' });
    setCookie(SEARCH_BEST_FOR_YOU_COOKIE, isNewBestForYouStateActive ? '' : 'active', { domain: cookieDomain });
    trackEvent('TE_PERSONALIZED_SEARCH_BFU_BUTTON', `${isNewBestForYouStateActive}`);
  };

  onToggleFacetsContainer = () => {
    const { isFacetsVisible, trackEvent } = this.props;
    const opening = !isFacetsVisible;
    this.runToggleFacetsContainer(opening);

    if (opening) {
      trackEvent('TE_SEARCH_OPENFILTERS');
    } else {
      trackEvent('TE_SEARCH_CLOSEFILTERS');
    }
  };

  resetFacet = () => {
    // toggles facets when screen hits tablet portrait
    this.runToggleFacetsContainer(false);
  };

  makeSeoCopyBottomPosition() {
    return this.makeSeoCopy(css.copyBottom);
  }

  makeSeoCopy(positionCSS) {
    const { testId } = this.context;
    const {
      filters: { seoData }
    } = this.props;

    const copy = seoData?.copy;
    if (copy) {
      return (
        <div className={cn(css.seoWrapper, positionCSS)} data-test-id={testId('seoWrapper')}>
          <HtmlToReact>{copy}</HtmlToReact>
        </div>
      );
    }
    return '';
  }

  toggleSeoCopy = () => {
    this.setState({ seoCopyCollapsed: !this.state.seoCopyCollapsed });
  };

  makeSeoText = () => {
    const {
      filters: { seoText, termLander, originalTerm },
      showSeoText
    } = this.props;
    const { testId } = this.context;

    if (showSeoText && (seoText || (termLander && originalTerm))) {
      return (
        <div className={css.seoWrapper}>
          {seoText && (
            <div className={css.seoText}>
              <p
                dangerouslySetInnerHTML={{
                  __html: sanitize(seoText)
                }}
              />
            </div>
          )}
          {termLander && originalTerm && (
            <p data-test-id={testId('termLanderMessage')}>
              We adjusted your search results to be more relevant.{' '}
              <Link to={`/search/null/orig/${originalTerm}`}>Not what you were looking for? Click here.</Link>
            </p>
          )}
        </div>
      );
    }
    return '';
  };

  // Tracks amplitude filter events with search context and user data
  fireAmplitudeFilterEvent = eventType => {
    const { filters, sessionId, products, userInfo } = this.props;
    const trackingData = getTrackingDataForFilterEvent({ eventType, filters, sessionId, products, userInfo });
    fireAmplitudeProductFilterEvent(trackingData);
  };

  onProductClicked = (productPosition, productId, styleId, productEventData) => {
    const { filters, sessionId, trackEvent, trackLegacyEvent } = this.props;
    const searchTerm = buildSearchTermEventPayload(filters.term);
    trackEvent('TE_SEARCH_CLICKPRODUCT', `${productId}:${styleId}:${productPosition}:${filters.page}`);
    // Duplicate event is expected #8854
    trackEvent('TE_SEARCH_CLICKTHROUGHPRODUCT', `${productId}:${styleId}:${productPosition}:${filters.page}`);
    trackLegacyEvent('Search-Results-Click-Through', formatProductClickEvent(filters.page, productPosition), searchTerm);
    trackLegacyEvent('Search-Results-Page', 'Results', productId);
    fireAmplitudeProductClickedEvent({
      event: AMPLITUDE_PRODUCT_CLICKED,
      product: productEventData,
      sessionId,
      term: searchTerm,
      [GTM_EVENT_KEY]: AMPLITUDE_PRODUCT_CLICKED_EVENT_ID
    });
  };

  onProductMediaHovered = (product, mainProductStyleId) => {
    track(() => [
      evProductInteract,
      {
        mainStyleId: mainProductStyleId,
        interactedProduct: product,
        interactionType: 'HOVER'
      }
    ]);
  };

  makeStickyHeader = () => {
    const { location } = this;
    const {
      filters,
      removePersonalizedSize,
      getFacetUrlParams,
      getFacetUrlPath,
      clearAutoComplete,
      products,
      isFacetsVisible,
      toggleFacetsContainer
    } = this.props;

    const clearFilters = () => {
      track(() => [evSearchClearFilters, {}]);
      this.clearFacetGroup();
    };

    return (
      <div className={css.stickyHeader}>
        <div className="flex w-full items-center gap-2 px-2 py-4 max-sm:justify-center md:hidden">
          <FacetsDrawer
            totalProductCount={products.totalProductCount}
            clearFilters={clearFilters}
            isFacetsVisible={isFacetsVisible}
            toggleFacetsContainer={toggleFacetsContainer}
          />
          <SortDrawer onSortSelected={this.onSortSelected} selectedSort={filters.sort} isBestForYouSortEligible={filters.bestForYouSortEligible} />
        </div>
        <div className={cn(css.desktopSearchMenu, css.pillsDesktopSearchMenu)}>
          <CompactSingleSelects
            // The key below ensures this component re-renders each time a page is navigated,
            // Forcing pill state in search to reset to the left
            key={location?.pathname}
            filters={filters}
            clearFilters={clearFilters}
            handlePillClick={this.handlePillClick}
            removePersonalizedSize={removePersonalizedSize}
            hasAutoScroll={true}
            getFacetUrlParams={getFacetUrlParams}
            getFacetUrlPath={getFacetUrlPath}
            clearAutoComplete={clearAutoComplete}
          />
        </div>
      </div>
    );
  };

  makeJanusPixel = () => {
    const {
      filters: { originalTerm },
      location
    } = this.props;
    const queryParams = {
      widget: 'RecordSearch',
      txt: originalTerm || 'no-term'
    };
    return <JanusPixel location={location} queryParams={queryParams} />;
  };

  onFacetGroupSelect = event => {
    const { toggleSelectedFacetGroup } = this.props;
    const { selectedFacetGroupName: groupName, selectedFacetSection: section } = event.currentTarget.dataset;
    toggleSelectedFacetGroup(groupName, section);
  };

  makeProducts = ({ isRecognizedCustomer, inlineBannerData } = {}) => {
    const { onProductClicked, onProductMediaHovered, onSortSelected, props, state, handleSearchFeedbackClick } = this;
    const {
      filters,
      location,
      products,
      showRatingStars,
      fetchProductDetail,
      heartProduct,
      unHeartProduct,
      toggleHeartingLoginModal,
      isFacetsVisible,
      trackSponsoredAdImpressions,
      crossSiteRecos,
      isLoadingSymphony,
      fetchSearchInlineRecos
    } = props;
    const { feedbackSubmitted } = state;

    return (
      <Products
        makeScrollButton={this.makeScrollButton}
        products={products}
        filters={filters}
        onSortSelected={onSortSelected}
        page={filters.page}
        onProductClicked={onProductClicked}
        onProductMediaHovered={onProductMediaHovered}
        showRatings={showRatings}
        showRatingStars={showRatingStars}
        feedbackSubmitted={feedbackSubmitted}
        handleSearchFeedbackClick={handleSearchFeedbackClick}
        inlineBannerData={inlineBannerData}
        isRecognizedCustomer={isRecognizedCustomer}
        trackSponsoredAdImpressions={trackSponsoredAdImpressions}
        getProductInfo={fetchProductDetail}
        location={location}
        heartProduct={heartProduct}
        unHeartProduct={unHeartProduct}
        toggleHeartingLoginModal={toggleHeartingLoginModal}
        isFacetsVisible={isFacetsVisible}
        crossSiteRecos={crossSiteRecos}
        isLoadingSymphony={isLoadingSymphony}
        fetchSearchInlineRecos={fetchSearchInlineRecos}
      />
    );
  };

  clearFacetGroup = () => {
    const { resetFacetGroup, getFacetUrlPath, getFacetUrlParams, filters, clearAllAutofacets } = this.props;
    const clearAllFiltersUrl = createClearAllFiltersQuery(filters, sortOptions);

    clearAllAutofacets(filters.breadcrumbs || []);

    getFacetUrlPath(clearAllFiltersUrl);
    getFacetUrlParams(clearAllFiltersUrl);
    resetFacetGroup(null);
  };

  handlePillClick = ({ name, value }, index) => {
    this.onFacetSelect(name, value, null, index);
  };

  saveFiltersRedirectToLogin = () => {
    const { cookieDomain, location, redirectToAuthenticationFor, filters } = this.props;
    const canSave = formatSavedFilters(filters);
    if (canSave) {
      setCookie(SAVI_SIZE_COOKIE, '1', { domain: cookieDomain });
    }
    redirectToAuthenticationFor(location);
  };

  onSaveSizeClick = () => {
    const { saveFilters, isRecognizedCustomer } = this.props;
    if (!isRecognizedCustomer) {
      this.saveFiltersRedirectToLogin();
    } else {
      saveFilters();
      trackEvent('TE_SAVED_FILTERS_SAVE_CLICK');
    }
  };

  onResetSizeClick = facetField => {
    const {
      deleteSavedFilters,
      filters: { savedsizes },
      saveFilters
    } = this.props;
    const savedFiltersWithValues = Object.values(savedsizes.filters).filter(v => v.length);
    const savedFilter = savedsizes.filters[facetField] || [];
    if (savedFilter.length) {
      if (savedFiltersWithValues.length > 1) {
        saveFilters(facetField);
      } else {
        if (savedsizes?.id) {
          deleteSavedFilters(savedsizes.id);
        }
      }
      trackEvent('TE_SAVED_FILTERS_RESET_CLICK');
    }
  };

  onSaveFeatureToggle = () => {
    const {
      cookieDomain,
      isRecognizedCustomer,
      toggleSavedFilters,
      filters: { applySavedFilters }
    } = this.props;
    if (!isRecognizedCustomer) {
      this.saveFiltersRedirectToLogin();
    } else {
      if (applySavedFilters) {
        trackEvent('TE_SAVED_FILTERS_TOGGLE_OFF');
      }
      setCookie(SAVI_COOKIE, applySavedFilters ? 'true' : '', {
        domain: cookieDomain
      });
      toggleSavedFilters();
    }
  };

  onFacetSelect = (facetGroup, facetName, selectedFacetGroupIndex, selectedFacetIndex, isPcm, section = null) => {
    const { filters, facets, togglePersonalizedSize } = this.props;
    if (facetGroup === BEST_FOR_YOU_FACETFIELD) {
      togglePersonalizedSize(facets);
      trackEvent('TE_PERSONALIZED_SEARCH_SIZE', `${!filters.personalizedSize?.facets?.[0]?.selected}`);
    } else {
      const { facets, filters, toggleSelectedFacet, trackEvent, trackLegacyEvent, clearAutoComplete } = this.props;

      if (filters.personalizedSize?.facets?.[0]?.selected && filters.personalizedSize?.sizes.indexOf(facetName) > -1) {
        togglePersonalizedSize();
      }

      toggleSelectedFacet(facetGroup, facetName, selectedFacetGroupIndex, selectedFacetIndex, section);
      clearAutoComplete(facetGroup);
      const facet = facets.navigation?.[selectedFacetGroupIndex];

      if (facet) {
        const facetGroupDisplayName = sanitizeForEvent(facet.facetFieldDisplayName);
        const sanitizedName = sanitizeForEvent(facetName);
        const searchTerm = buildSearchTermEventPayload(filters.term);
        trackLegacyEvent('Search-Results-Page', `Facet-Click-${facetGroupDisplayName}-${sanitizedName}`, searchTerm);
        trackLegacyEvent('Search-Results-Page', `FCT${facetGroup.toLowerCase()}`, sanitizedName);
        trackEvent('TE_SEARCH_FILTERS', `${facetGroupDisplayName}:${facetName}${isPcm ? ':pcm:true' : ''}`);
      }
    }
  };

  handleSearchFeedbackClick = feedback => {
    this.setState({ feedbackSubmitted: true });
    this.props.searchFeedbackClick(feedback);
  };

  makePersonalizedMessage = (isCompact = false) => {
    const { testId } = this.context;
    const {
      filters: { sort, bestForYouSortEligible }
    } = this.props;
    const optedText = !sort.bestForYou ? 'not ' : '';
    const toggleText = `Results are ${optedText}sorted based on your Preferences. `;

    if (bestForYouSortEligible) {
      return (
        <>
          <Tooltip
            tooltipId="personalizedSearch"
            wrapperClassName={css.tooltipWrapper}
            tooltipClassName={css.tooltip}
            direction="left"
            content={
              "Your own special blend is here! We're combining your feedback, recent purchases, and shopping behavior to sort your best results to the top."
            }
          >
            <InfoOutlineSmallIcon size={16} />
          </Tooltip>
          <span>
            {!isCompact && toggleText}
            <button
              type="button"
              onClick={this.handlePersonalizedBestForYou}
              data-test-id={testId('bestForYouButton')}
              aria-label={`Turn ${sort.bestForYou ? 'off' : 'on'} best for you sort.`}
            >
              Turn {sort.bestForYou ? 'off' : 'on'}
            </button>
          </span>
        </>
      );
    }
  };

  makePersonalizedSortToggle = (testIdName, isFooter = false, isCompact = false) => {
    const { testId } = this.context;
    return (
      <div data-test-id={testId(testIdName)} className={cn(css.hpsMessage, { [css.footer]: isFooter })}>
        {this.makePersonalizedMessage(isCompact)}
      </div>
    );
  };

  render() {
    const {
      facets,
      filters,
      isFacetsVisible,
      location,
      params,
      products,
      topBannerData,
      inlineBannerData,
      isRecognizedCustomer,
      fetchSearchInlineRecos,
      redirectWithAppRoot,
      toggleFacetsContainer,
      clearInlineRecos,
      setOosMessaging,
      getHearts,
      pageTypeChange,
      setHFSearchTerm,
      setAndStoreCookie,
      sessionExpiration,
      fetchFromZso,
      fetchFromSearch,
      fetchSymphonySearchComponents,
      setUrlUpdated,
      isVip,
      landingPage,
      fetchLandingPageInfo,
      filter,
      isResponseFilter,
      fireSearchPixels,
      pixelFacetData,
      symphonyPageLayout
    } = this.props;
    const { testId } = this.context;
    const isLoading = !!products.isLoading;

    return (
      <SiteAwareMetadata loading={isLoading || !products.executedSearchUrl}>
        <SearchLogic
          makeScrollhandler={this.makeScrollhandler}
          location={location}
          params={params}
          fetchSearchInlineRecos={fetchSearchInlineRecos}
          products={products}
          facets={facets}
          filters={filters}
          isFacetsVisible={isFacetsVisible}
          redirectWithAppRoot={redirectWithAppRoot}
          toggleFacetsContainer={toggleFacetsContainer}
          clearInlineRecos={clearInlineRecos}
          setOosMessaging={setOosMessaging}
          getHearts={getHearts}
          pageTypeChange={pageTypeChange}
          setHFSearchTerm={setHFSearchTerm}
          setAndStoreCookie={setAndStoreCookie}
          sessionExpiration={sessionExpiration}
          fetchFromZso={fetchFromZso}
          fetchFromSearch={fetchFromSearch}
          fetchSymphonySearchComponents={fetchSymphonySearchComponents}
          setUrlUpdated={setUrlUpdated}
          fetchLandingPageInfo={fetchLandingPageInfo}
          landingPage={landingPage}
          isVip={isVip}
          fireSearchPixels={fireSearchPixels}
          pixelFacetData={pixelFacetData}
        >
          {({ hasProductResults, shouldShowNoResults }) => (
            <div
              ref={e => (this.stickyPlaceholder = e)}
              className={cn(css.wrap, 'searchWrapper', 'searchFacets')}
              data-test-id={testId('searchPageWrapper')}
              data-layout={symphonyPageLayout}
              data-page-id={filters.term}
            >
              {!shouldShowNoResults() && (
                <div data-test-id={testId('searchResults')} className={css.searchResults}>
                  <FacetsProvider hasAutoComplete={true} onFacetSelect={this.onFacetSelect}>
                    {this.makeAccessibilityAnchors(hasProductResults)}
                    <GamSlot slot={SRP_WIDE_TOP} className={css.displayAd} />
                    <GamSlot slot={SRP_NARROW_TOP} />
                    <SearchHeader
                      searchHeaderRef={this.searchHeaderRef}
                      searchFilter={filter}
                      isResponseFilter={isResponseFilter}
                      searchTerm={filters.term}
                      totalProductCount={products.totalProductCount}
                      onSortSelected={this.onSortSelected}
                      filters={filters}
                      makePersonalizedSortToggle={this.makePersonalizedSortToggle}
                      isFacetsVisible={isFacetsVisible}
                      onToggleFacetsContainer={this.onToggleFacetsContainer}
                    />
                    {topBannerData && (
                      <MelodyCategory slotDetails={topBannerData} slotIndex={0} slotName={SYMPHONY_TOP_HEADER_SLOT_NAME} isOnSearchPage />
                    )}
                    {topBannerData && (
                      <TopBannerAd
                        className="searchPage"
                        slotDetails={topBannerData}
                        searchTerm={filters.term}
                        selectedFilters={filters.selected}
                        isFacetsVisible={isFacetsVisible}
                      />
                    )}
                    {this.makeStickyHeader()}
                    {this.makeSeoText()}
                    {this.makeProducts({ isRecognizedCustomer, inlineBannerData })}
                    <aside id="searchFilters" className={'facetWrapper facets'} ref={c => (this.facets = c)}>
                      <FacetsSidebar />
                      <GamSlot slot={SRP_WIDE_MID} className={css.adContainerOverride} />
                    </aside>

                    {this.makeJanusPixel()}
                    {!isLoading && this.makeSearchFooter({ hasProductResults })}
                    <div className="searchPage">
                      <BannerAdInTest className="mb-8 mt-2" />
                    </div>
                  </FacetsProvider>
                </div>
              )}
            </div>
          )}
        </SearchLogic>
      </SiteAwareMetadata>
    );
  }
}

StandardSearch.defaultProps = {
  trackEvent,
  trackLegacyEvent
};

StandardSearch.contextTypes = {
  testId: PropTypes.func.isRequired,
  marketplace: PropTypes.object.isRequired
};

StandardSearch.propTypes = {
  products: PropTypes.object.isRequired,
  filters: PropTypes.object
};

function mapStateToProps(state) {
  const {
    autoComplete,
    autofacet,
    cookies,
    customer: isRecognizedCustomer,
    facets,
    filters,
    isFacetsVisible,
    search: {
      symphony: { slotData, pageLayout: symphonyPageLayout },
      isLoadingSymphony
    },
    headerFooter: { filter, isResponseFilter },
    products,
    url: { userAgent },
    killswitch: { isShowingThirdPartyAds },
    landingPage,
    rewards,
    ads: { adCustomerId, adEmailHash },
    holmes: { customerId, directedId }
  } = state;
  const pixelFacetData = makeSearchPixelFacetDataFromState(state);

  const sessionId = state.cookies['session-id'];

  const isVip = rewards?.rewardsInfo?.isVipOrConsented;

  const userInfo = {
    login_status: isRecognizedCustomer ? 'logged-in' : 'logged-out',
    login_type: customerId ? 'amazon' : '',
    vip_status: isVip ? 'enrolled' : '',
    directedID: directedId,
    zaw_status: ''
  };

  return {
    autoComplete,
    autofacet,
    cookieDomain,
    facets,
    filters,
    isFacetsVisible,
    isShowingThirdPartyAds,
    isRecognizedCustomer,
    landingPage,
    products,
    saviSizeCookie: cookies[SAVI_SIZE_COOKIE] || null,
    showRatingStars,
    showSeoText,
    topBannerData: slotData?.[SYMPHONY_TOP_HEADER_SLOT_NAME],
    symphonyPageLayout,
    inlineBannerData: slotData?.['search-results-1'],
    isLoadingSymphony: isLoadingSymphony,
    isVip,
    userAgent,
    adCustomerId,
    adEmailHash,
    customerId,
    filter,
    isResponseFilter,
    pixelFacetData,
    sessionId,
    userInfo
  };
}

export const mapDispatchToProps = {
  addAdToQueue,
  clearAllAutofacets,
  clearAutoComplete,
  deleteSavedFilters,
  fetchProductDetail,
  heartProduct,
  redirectToAuthenticationFor,
  removeAutofacetFilter,
  removeFromStoredCookies,
  removePersonalizedSize,
  resetFacetGroup,
  toggleHeartingLoginModal,
  toggleFacetsContainer,
  togglePersonalizedSize,
  toggleSelectedFacet,
  toggleSelectedFacetGroup,
  trackSponsoredAdImpressions,
  updateBestForYou,
  unHeartProduct,
  updateSort,
  getFacetUrlParams,
  getFacetUrlPath,
  searchFeedbackClick,
  saveFilters,
  toggleSavedFilters,
  fetchSearchInlineRecos,
  redirectWithAppRoot,
  clearInlineRecos,
  setOosMessaging,
  getHearts,
  pageTypeChange,
  setHFSearchTerm,
  setAndStoreCookie,
  sessionExpiration,
  fetchFromZso,
  fetchFromSearch,
  fetchSymphonySearchComponents,
  setUrlUpdated,
  fetchLandingPageInfo,
  fetchAccountInfo,
  updateAdData,
  fetchCustomerAuthDetails,
  fireSearchPixels,
  firePixelEvent
};

export default connect(mapStateToProps, mapDispatchToProps)(StandardSearch);
